import { Logger } from "@aws-amplify/core";

const logger = new Logger("AbTest");

const EXPERIMENT_NAME = "booking_flow_v2";
const STABLE_ID_COOKIE = "mb_stable_id";
const ENTRY_SOURCE_COOKIE = "mb_ab_entry";
const ENTRY_SOURCE_MARKETING = "marketing_site";
const ENTRY_SOURCE_DIRECT = "direct";
const SIGNED_IN_IDENTITY_KEY = "mb_ab_identity";
const COOKIE_MAX_AGE_SECONDS = 90 * 24 * 60 * 60;
const STATSIG_INIT_TIMEOUT_MS = 2000;

let statsigClientPromise = null;
let entrySourceCache = null;
const loggedOnceEvents = new Set();
// Serializes updateUserAsync calls and lets logAbEvent wait for the latest
// identity before logging, so events land on the right Statsig user.
let identityChain = Promise.resolve();

function abConfig() {
  return {
    host: process.env.REACT_APP_AB_HOST,
    v2Url: process.env.REACT_APP_AB_V2_URL,
    clientKey: process.env.REACT_APP_STATSIG_CLIENT_KEY,
    entryDomain: process.env.REACT_APP_AB_ENTRY_DOMAIN,
  };
}

// The experiment only ever runs for the single tenant hostname configured in
// REACT_APP_AB_HOST; every other tenant short-circuits here, before any
// Statsig code is even downloaded.
export function isAbHost() {
  const { host, v2Url, clientKey } = abConfig();
  return Boolean(
    host && v2Url && clientKey && window.location.hostname === host,
  );
}

function getCookie(name) {
  const row = document.cookie
    .split("; ")
    .find((entry) => entry.startsWith(name + "="));
  return row ? decodeURIComponent(row.split("=").slice(1).join("=")) : null;
}

// A/B cookies are written on the registrable domain (e.g. .aquamobileswim.com)
// so the v1 and v2 subdomains read the same values.
function writeCookie(name, value) {
  const hostname = window.location.hostname;
  let cookie =
    name +
    "=" +
    encodeURIComponent(value) +
    "; Max-Age=" +
    COOKIE_MAX_AGE_SECONDS +
    "; Path=/; SameSite=Lax";
  if (hostname !== "localhost") {
    const registrableDomain = hostname.split(".").slice(-2).join(".");
    cookie += "; Domain=" + registrableDomain + "; Secure";
  }
  document.cookie = cookie;
}

// The stable id lives in a cookie on the registrable domain so the v1 and v2
// subdomains bucket and log events against the same Statsig unit. Statsig's
// built-in stableID cannot be used for this: it is stored in localStorage,
// which is per-origin.
function readOrCreateStableId() {
  let stableId = getCookie(STABLE_ID_COOKIE);
  if (!stableId) {
    stableId =
      window.crypto && window.crypto.randomUUID
        ? window.crypto.randomUUID()
        : Date.now() + "-" + Math.random().toString(36).slice(2);
  }
  writeCookie(STABLE_ID_COOKIE, stableId);
  return stableId;
}

// The marketing site whose "Book Now" button feeds the booking flow. Defaults
// to the registrable domain of the experiment host, e.g.
// register.aquamobileswim.com -> aquamobileswim.com.
function marketingDomain() {
  const { host, entryDomain } = abConfig();
  if (entryDomain) return entryDomain;
  if (!host) return null;
  return host.split(".").slice(-2).join(".");
}

function isMarketingSiteArrival() {
  // Local development and QA cannot produce a real cross-site referrer.
  if (
    window.location.hostname === "localhost" &&
    new URLSearchParams(window.location.search).get("mb_ab_entry") === "web"
  ) {
    return true;
  }
  const domain = marketingDomain();
  if (!domain || !document.referrer) return false;
  try {
    const referrerHost = new URL(document.referrer).hostname;
    // Exact match only: register./register2. referrers are our own hosts and
    // must not count as an arrival from the marketing site.
    return referrerHost === domain || referrerHost === "www." + domain;
  } catch (e) {
    return false;
  }
}

function readEntrySource() {
  const remembered = getCookie(ENTRY_SOURCE_COOKIE);
  if (remembered) return remembered;
  if (isMarketingSiteArrival()) {
    // Only eligibility is ever remembered: someone who lands here directly
    // today may still click "Book Now" tomorrow.
    writeCookie(ENTRY_SOURCE_COOKIE, ENTRY_SOURCE_MARKETING);
    return ENTRY_SOURCE_MARKETING;
  }
  return ENTRY_SOURCE_DIRECT;
}

// Only visitors who arrived from the marketing site are randomised into the
// experiment; everyone else (bookmarks, search results, email links) stays on
// v1 and never generates an exposure. The verdict is remembered in a cookie on
// the registrable domain so a refresh or a later bookmarked visit keeps the
// same experience.
function entrySource() {
  if (!entrySourceCache) {
    entrySourceCache = readEntrySource();
  }
  return entrySourceCache;
}

function isExperimentEligible() {
  return isAbHost() && entrySource() === ENTRY_SOURCE_MARKETING;
}

function buildStatsigUser(identity) {
  const user = {
    customIDs: { stableID: readOrCreateStableId() },
    custom: { entry_source: entrySource() },
  };
  if (identity && identity.clientId) {
    user.userID = identity.clientId;
    if (identity.email) {
      user.email = identity.email;
    }
  }
  return user;
}

function readCachedIdentity() {
  try {
    const raw = window.localStorage.getItem(SIGNED_IN_IDENTITY_KEY);
    if (!raw) return null;
    const cached = JSON.parse(raw);
    return cached && cached.clientId && cached.email ? cached : null;
  } catch (e) {
    return null;
  }
}

function writeCachedIdentity(identity) {
  try {
    if (identity && identity.clientId) {
      window.localStorage.setItem(
        SIGNED_IN_IDENTITY_KEY,
        JSON.stringify({ clientId: identity.clientId, email: identity.email }),
      );
    } else {
      window.localStorage.removeItem(SIGNED_IN_IDENTITY_KEY);
    }
  } catch (e) {
    // Storage may be unavailable (private mode, quota); identity still
    // applies to the current page load, it just won't survive a reload.
  }
}

function getStatsigClient() {
  if (!statsigClientPromise) {
    statsigClientPromise = (async () => {
      const { StatsigClient } = await import("@statsig/js-client");
      const client = new StatsigClient(
        abConfig().clientKey,
        buildStatsigUser(null),
      );
      // If Statsig is slow or unreachable, proceed uninitialized: experiment
      // checks then return their defaults, which keeps the user on v1.
      await Promise.race([
        client.initializeAsync(),
        new Promise((resolve) => setTimeout(resolve, STATSIG_INIT_TIMEOUT_MS)),
      ]);
      return client;
    })();
  }
  return statsigClientPromise;
}

// Returns true when this visit belongs to the v2 cohort and has been
// redirected; the caller must stop all further work. Any failure keeps the
// user on v1.
export async function maybeRedirectToV2() {
  if (!isExperimentEligible()) return false;
  try {
    const client = await getStatsigClient();
    const experiment = client.getExperiment(EXPERIMENT_NAME);
    if (!experiment.get("use_v2", false)) return false;
    const { v2Url } = abConfig();
    const target =
      v2Url.replace(/\/+$/, "") +
      window.location.pathname +
      window.location.search;
    // Records exactly which units were sent to v2: register2 cannot tell a
    // redirected visitor from someone who opened it directly.
    await logAbEvent("redirected_to_v2", null, {
      stable_id: readOrCreateStableId(),
      target,
    });
    if (typeof client.flush === "function") {
      // Deliver the exposure and the event above before leaving the page.
      await client.flush();
    }
    window.location.replace(target);
    return true;
  } catch (e) {
    logger.error("A/B redirect check failed; staying on v1", e);
    return false;
  }
}

// Attaches the signed-in customer to all subsequent Statsig events:
// userID = MarketBox client id, plus the customer's email. Pass null on
// sign-out to fall back to the anonymous stable id. The guest Cognito
// account must never be passed here.
export function setAbSignedInIdentity(identity) {
  if (!isAbHost()) return Promise.resolve();
  // Cache the identity so the next page load can re-attach it before any
  // events fire; cleared when identity is cleared.
  writeCachedIdentity(identity);
  identityChain = identityChain.then(async () => {
    try {
      const client = await getStatsigClient();
      // updateUserAsync replaces the whole user object, so the stable id and
      // the entry source have to be rebuilt alongside the identity.
      const statsigUser = buildStatsigUser(identity);
      // updateUserAsync switches the user immediately and refreshes values in
      // the background; the race only bounds how long we wait for it.
      await Promise.race([
        client.updateUserAsync(statsigUser),
        new Promise((resolve) => setTimeout(resolve, STATSIG_INIT_TIMEOUT_MS)),
      ]);
    } catch (e) {
      logger.warn("Unable to update A/B identity", e);
    }
  });
  return identityChain;
}

// Re-attaches the identity cached at the last sign-in, provided it matches
// the email of the restored Cognito session; a stale or mismatched cache is
// dropped. Pass a falsy email when the restored session is the guest user.
export function attachCachedAbIdentity(signedInEmail) {
  if (!isAbHost()) return;
  try {
    const cached = readCachedIdentity();
    if (!cached) return;
    if (signedInEmail && cached.email === signedInEmail) {
      setAbSignedInIdentity(cached);
    } else {
      writeCachedIdentity(null);
    }
  } catch (e) {
    logger.warn("Unable to attach cached A/B identity", e);
  }
}

// No-op on every hostname except the experiment host.
export async function logAbEvent(eventName, value = null, metadata = null) {
  if (!isAbHost()) return;
  try {
    await identityChain;
    const client = await getStatsigClient();
    // Tag every event with the originating site so v1 and v2 events are
    // distinguishable in Statsig; register2 logs site: "v2". Caller metadata
    // is spread last so an explicit value wins.
    client.logEvent(eventName, value, {
      site: "v1",
      host: window.location.hostname,
      ...(metadata || {}),
    });
  } catch (e) {
    logger.warn("Unable to log A/B event " + eventName, e);
  }
}

// Like logAbEvent, but at most once per page load (init() and some effects
// run more than once).
export function logAbEventOnce(eventName, value = null, metadata = null) {
  if (loggedOnceEvents.has(eventName)) return;
  loggedOnceEvents.add(eventName);
  logAbEvent(eventName, value, metadata);
}
